import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  OutlinedInput,
  IconButton,
  ThemeProvider,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { generalSelectTheme, invertedButtonTheme } from 'assets/styles/themes/tabs_mui';
import { generalTheme } from 'assets/styles/themes/general_theme';

const createEmptyFilter = (showTargetSelect) => ({
  id: Date.now(),
  ...(showTargetSelect && { targets: [] }),
  property: '',
  values: [],
  textValue: '',
});

/**
 * Composant générique de panel de filtres
 *
 * @param {Object} props
 * @param {Array} props.filterProperties
 * @param {Array} props.activeFilters
 * @param {Function} props.onApply
 * @param {Function} props.onReset
 * @param {Function} props.onClose
 * @param {boolean} props.showTargetSelect
 * @param {Function} props.formatFiltersOutput
 * @param {number} props.minWidth
 */
const FilterPanel = ({
  filterProperties = [],
  activeFilters = [],
  onApply,
  onReset,
  onClose,
  showTargetSelect = false,
  formatFiltersOutput,
  minWidth = 500,
}) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(() => {
    if (activeFilters && activeFilters.length > 0) {
      return activeFilters.map((f) => ({
        ...f,
        ...(f.targets && { targets: [...f.targets] }),
        values: [...(f.values || [])],
      }));
    }
    return [createEmptyFilter(showTargetSelect)];
  });

  const getAvailableValues = (property) => {
    if (!property) return [];
    const prop = filterProperties.find((p) => p.field === property);
    return prop?.options || [];
  };

  const getPropertyType = (property) => {
    if (!property) return 'select';
    const prop = filterProperties.find((p) => p.field === property);
    return prop?.type || 'select';
  };

  const getAvailableProperties = (currentFilterId) => {
    const usedProperties = filters
      .filter((f) => f.id !== currentFilterId && f.property)
      .map((f) => f.property);

    return filterProperties.filter((p) => !usedProperties.includes(p.field));
  };

  const handleFilterTargetsChange = (filterId, event) => {
    const value = event.target.value;
    const newTargets = typeof value === 'string' ? value.split(',') : value;
    setFilters((prev) => prev.map((f) => (f.id === filterId ? { ...f, targets: newTargets } : f)));
  };

  const handlePropertyChange = (filterId, event) => {
    const value = event.target.value;
    setFilters((prevFilters) =>
      prevFilters.map((f) =>
        f.id === filterId ? { ...f, property: value, values: [], textValue: '' } : f,
      ),
    );
  };

  const handleValuesChange = (filterId, event) => {
    const value = event.target.value;
    const newValues = typeof value === 'string' ? value.split(',') : value;
    setFilters((prevFilters) =>
      prevFilters.map((f) => (f.id === filterId ? { ...f, values: newValues } : f)),
    );
  };

  const handleTextChange = (filterId, event) => {
    const value = event.target.value;
    setFilters((prevFilters) =>
      prevFilters.map((f) => (f.id === filterId ? { ...f, textValue: value } : f)),
    );
  };

  const handleAddFilter = () => {
    setFilters([...filters, createEmptyFilter(showTargetSelect)]);
  };

  const handleRemoveFilter = (filterId) => {
    if (filters.length === 1) {
      setFilters([createEmptyFilter(showTargetSelect)]);
    } else {
      setFilters(filters.filter((f) => f.id !== filterId));
    }
  };

  const handleApply = () => {
    let output;

    if (formatFiltersOutput) {
      output = formatFiltersOutput(filters);
    } else {
      output = filters;
    }

    onApply(output);
    onClose?.();
  };

  const handleReset = () => {
    setFilters([createEmptyFilter(showTargetSelect)]);
    onReset();
    onClose?.();
  };

  const isApplyDisabled = filters.every((f) => {
    if (!f.property) return true;
    if (showTargetSelect && (!f.targets || f.targets.length === 0)) return true;
    if (getPropertyType(f.property) === 'text') return !f.textValue;
    return f.values.length === 0;
  });

  const isAddDisabled = filters.filter((f) => f.property).length >= filterProperties.length;

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth }}>
      {filters.map((filter) => (
        <Box
          key={filter.id}
          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}
        >
          {/* Select Lignes/Colonnes (optionnel) */}
          {showTargetSelect && (
            <ThemeProvider theme={generalSelectTheme}>
              <FormControl size="small" sx={{ width: 170 }}>
                <InputLabel>{t('filterPanel.filterOn')}</InputLabel>
                <Select
                  multiple
                  value={filter.targets || []}
                  onChange={(e) => handleFilterTargetsChange(filter.id, e)}
                  input={<OutlinedInput label={t('filterPanel.filterOn')} />}
                  renderValue={(selected) =>
                    selected
                      .map((value) =>
                        value === 'rows' ? t('common.rows') : t('common.columns'),
                      )
                      .join(', ')
                  }
                >
                  <MenuItem value="rows">
                    <Checkbox checked={filter.targets?.includes('rows')} />
                    <ListItemText primary={t('common.rows')} />
                  </MenuItem>
                  <MenuItem value="columns">
                    <Checkbox checked={filter.targets?.includes('columns')} />
                    <ListItemText primary={t('common.columns')} />
                  </MenuItem>
                </Select>
              </FormControl>
            </ThemeProvider>
          )}

          {/* Select Propriété */}
          <ThemeProvider theme={generalSelectTheme}>
            <FormControl size="small" sx={{ width: 170 }}>
              <InputLabel>{t('filterPanel.property')}</InputLabel>
              <Select
                value={filter.property}
                onChange={(e) => handlePropertyChange(filter.id, e)}
                label={t('filterPanel.property')}
              >
                {getAvailableProperties(filter.id).map((property) => (
                  <MenuItem key={property.field} value={property.field}>
                    {property.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </ThemeProvider>

          {/* Champ Valeur (TextField ou Select selon le type) */}
          {getPropertyType(filter.property) === 'text' ? (
            <TextField
              size="small"
              sx={{ width: 275 }}
              label={t('filterPanel.value')}
              value={filter.textValue || ''}
              onChange={(e) => handleTextChange(filter.id, e)}
              disabled={!filter.property}
              placeholder={t('filterPanel.searchPlaceholder')}
            />
          ) : (
            <ThemeProvider theme={generalSelectTheme}>
              <FormControl size="small" sx={{ width: 275 }} disabled={!filter.property}>
                <InputLabel>{t('filterPanel.values')}</InputLabel>
                <Select
                  multiple
                  value={filter.values}
                  onChange={(e) => handleValuesChange(filter.id, e)}
                  input={<OutlinedInput label={t('filterPanel.values')} />}
                  renderValue={(selected) => {
                    return selected
                      .map((value) => {
                        const option = getAvailableValues(filter.property).find(
                          (o) => o.value === value,
                        );
                        return option?.label || value;
                      })
                      .join(', ');
                  }}
                >
                  {getAvailableValues(filter.property).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={filter.values.includes(option.value)} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </ThemeProvider>
          )}

          {/* Bouton Supprimer */}
          <IconButton size="small" onClick={() => handleRemoveFilter(filter.id)} color="error">
            <DeleteIcon sx={{ paddingLeft: 0 }} />
          </IconButton>
        </Box>
      ))}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <ThemeProvider theme={invertedButtonTheme}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddFilter}
            disabled={isAddDisabled}
          >
            {t('filterPanel.addFilter')}
          </Button>
        </ThemeProvider>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <ThemeProvider theme={invertedButtonTheme}>
            <Button variant="outlined" size="small" onClick={handleReset}>
              {t('filterPanel.reset')}
            </Button>
          </ThemeProvider>
          <ThemeProvider theme={generalTheme}>
            <Button
              variant="contained"
              size="small"
              onClick={handleApply}
              disabled={isApplyDisabled}
            >
              {t('filterPanel.apply')}
            </Button>
          </ThemeProvider>
        </Box>
      </Box>
    </Box>
  );
};

export default FilterPanel;
